import i18next, { type TFunction } from 'i18next';
import { type DerogationDto, type PolicyDtoStatusEnumKey } from 'korint-sdk';
import {
  AWAITING_FIRST_PAYMENT,
  STOP_AWAITING_VALIDATION,
  STOP_SCHEDULED,
  useKorintConfig,
} from 'korint-sdk/react';
import { usePolicyStatuses } from '../../hooks/usePolicyStatuses';
import { StatusCell } from '../module-elements/PolicyTable/StatusCell';
import { PendingStopChip, hasPendingStop } from './PendingStopChip';
import { AwaitingFirstPaymentChip } from './AwaitingFirstPaymentChip';

type PolicyStatusProps = {
  policy: {
    status: PolicyDtoStatusEnumKey;
    stoppedAt?: string;
    productId?: string;
    stop?: { reason?: string; stopDate?: string; executesAt?: string };
    isStopAwaitingValidation?: boolean;
    customFields?: Record<string, unknown> | Array<{ key: string; value: unknown }>;
    isAwaitingFirstPayment?: boolean;
  };
  hasSigned?: boolean;
  workingBranch?: { derogations?: DerogationDto[] };
};

// A status with no translation falls back to its humanized enum ("policy started"),
// never the raw `policyStatus.X` key.
const humanizeStatus = (status: string) =>
  status
    .replace(/^POLICY_/, '')
    .replace(/_/g, ' ')
    .toLowerCase();

function joinStatusLabels(statuses: PolicyDtoStatusEnumKey[], t: TFunction) {
  const separator = t('policyStatus.listSeparator');
  return statuses
    .map(
      status =>
        `"${t(`policyStatus.${status}`, { defaultValue: humanizeStatus(status) }).toLowerCase()}"`,
    )
    .join(', ')
    .replace(/\,(?=[^,]*$)/, ` ${separator} `);
}

export function expectedPolicyStatusConflictMessage(
  expectedStatuses: PolicyDtoStatusEnumKey[],
  t: TFunction,
) {
  return t('policyStatus.expectedConflict', { labels: joinStatusLabels(expectedStatuses, t) });
}

export function unexpectedPolicyStatusConflictMessage(
  unexpectedStatuses: PolicyDtoStatusEnumKey[],
  t: TFunction,
) {
  return t('policyStatus.unexpectedConflict', { labels: joinStatusLabels(unexpectedStatuses, t) });
}

// Builds the `userHelp` for a CONFLICTING_POLICY_STATUS error. `policyStatus` is one
// status or several (the API lists every status that would have allowed the action).
export function policyStatusConflictUserHelp(details: {
  policyStatus: PolicyDtoStatusEnumKey | PolicyDtoStatusEnumKey[];
  requirement: string;
}): string {
  const statuses = Array.isArray(details.policyStatus)
    ? details.policyStatus
    : [details.policyStatus];
  const t = i18next.t.bind(i18next);
  return details.requirement === 'must be'
    ? expectedPolicyStatusConflictMessage(statuses, t)
    : unexpectedPolicyStatusConflictMessage(statuses, t);
}

export function getDerogationOverriddenStatus(
  status: PolicyDtoStatusEnumKey,
  derogations?: DerogationDto[],
): PolicyDtoStatusEnumKey | 'QUOTE_TO_BE_APPROVED' | 'DEROGATION_APPROVED' {
  const isDerogationCreated = derogations?.some(
    ({ status: derogationStatus }) => derogationStatus === 'CREATED',
  );
  const isDerogationApproved = derogations?.some(
    ({ status: derogationStatus }) => derogationStatus === 'APPROVED',
  );

  if (status === 'POLICY_QUOTED' && isDerogationCreated) {
    return 'QUOTE_TO_BE_APPROVED';
  }

  if (status === 'POLICY_QUOTED' && isDerogationApproved) {
    return 'DEROGATION_APPROVED';
  }

  return status;
}

type PolicyStatusInput = PolicyStatusProps['policy'];

/**
 * @debt Yeet-specific: Yeet uses customFields.customStatus for fine-grained CRM stages
 * (e.g. IN_DISCOVERY, QUOTE_SENT) that map to a single standard policy status (e.g. POLICY_CREATED).
 * When the custom status is recognized in policyStatuses and is consistent with the current
 * standard status, we display it instead for better granularity.
 * This should be replaced by a proper backend solution where branches carry their own granular status.
 */
const CUSTOM_STATUS_TO_STANDARD: Record<string, string> = {
  DRAFT: 'POLICY_CREATED',
  IN_DISCOVERY: 'POLICY_CREATED',
  QUOTE_TO_BE_ESTABLISHED: 'POLICY_CREATED',
  QUOTE_SENT: 'POLICY_QUOTED',
  CONTRACT_SENT: 'POLICY_CONFIRMED',
  CONTRACT_SIGNED: 'POLICY_SIGNED',
  ACTIVE_POLICY: 'POLICY_STARTED',
  STOPPED_POLICY: 'POLICY_STOPPED',
  SUSPENDED_POLICY: 'POLICY_SUSPENDED',
};

export const getPolicyCustomStatusKey = (customFields: PolicyStatusInput['customFields']) =>
  Array.isArray(customFields)
    ? (customFields as Array<{ key: string; value: string }>).find(f => f.key === 'customStatus')
        ?.value
    : typeof customFields?.customStatus === 'string'
      ? customFields.customStatus
      : undefined;

/**
 * The status the user actually reads in the pill, pseudo-statuses included. Single source of truth
 * shared with PolicyTable's row filtering, so what is filtered is exactly what is displayed.
 */
export function getDisplayedPolicyStatus({
  policy,
  branchDerogations,
  getPolicyStatusOverride,
  policyStatuses,
}: {
  policy: PolicyStatusInput;
  branchDerogations?: DerogationDto[];
  getPolicyStatusOverride?: (
    status: PolicyDtoStatusEnumKey,
    stopReason?: string,
  ) => string | undefined;
  policyStatuses: Record<string, string>;
}): string {
  const { status, stop, customFields, isAwaitingFirstPayment } = policy;

  if (isAwaitingFirstPayment) {
    return AWAITING_FIRST_PAYMENT;
  }

  if (hasPendingStop(status, policy)) {
    return policy.isStopAwaitingValidation ? STOP_AWAITING_VALIDATION : STOP_SCHEDULED;
  }

  const customOverride = getPolicyStatusOverride?.(status, stop?.reason);
  const overriddenStatus = customOverride
    ? customOverride
    : getDerogationOverriddenStatus(status, branchDerogations);

  const customStatusKey = getPolicyCustomStatusKey(customFields);
  if (
    customStatusKey &&
    policyStatuses[customStatusKey] &&
    CUSTOM_STATUS_TO_STANDARD[customStatusKey] === overriddenStatus
  ) {
    return customStatusKey;
  }

  return overriddenStatus;
}

export const PolicyStatus = ({ policy, workingBranch }: PolicyStatusProps) => {
  const korintConfig = useKorintConfig().korintConfig;
  const policyStatuses = usePolicyStatuses();

  const displayStatus = getDisplayedPolicyStatus({
    policy,
    branchDerogations: workingBranch?.derogations,
    getPolicyStatusOverride: korintConfig.getPolicyStatusOverride,
    policyStatuses,
  });

  if (displayStatus === AWAITING_FIRST_PAYMENT) {
    return <AwaitingFirstPaymentChip />;
  }

  if (displayStatus === STOP_SCHEDULED || displayStatus === STOP_AWAITING_VALIDATION) {
    return <PendingStopChip awaitingValidation={displayStatus === STOP_AWAITING_VALIDATION} />;
  }

  return <StatusCell value={displayStatus} policyProductId={policy.productId} />;
};
